/** @flow */

import React, { Component } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import classNames from "classnames";
import moment from "moment";
import { formatDateTime } from "@transformers/date";
import config from "@config";
import {
  GSGrid,
  Pagination,
  Loader,
  SectionComponent,
  Modal,
  View,
  Button,
  ConfirmationModal,
} from "@components";
import type { State } from "@store/reducers";
import {
  TasksQueueFilter,
  AbsenceTaskDetailsSection,
  ManagerReferralDecisionTask,
  RTWTaskDetailsSection,
  ModalAbsenceReason,
} from "@modules/reporting/components";
import * as selectors from "@modules/reporting/selectors/tasks";
import actionCreators from "@modules/reporting/actionCreator";
import type { SelectedTask, OutstandingTask } from "@modules/reporting/reducer/tasks/types";
import { selectUserType } from "@modules/account/selectors";
import { route } from "@routes/helper";

import "./tasks-page.scss";
import type { PaginationExtendedType } from "@types";

type PropsFromState = $Exact<{
  total: number,
  selectedTask: ?SelectedTask,
  isLoading: boolean,
  tasksSearch: string,
  tasksType: string | null,
  userType: string,
}>;

type PropsFromDispatch = $Exact<{
  setMyTasksSearch: typeof actionCreators.SetMyTasksSearch,
  setMyTasksType: typeof actionCreators.SetMyTasksType,
  submitCancelTask: typeof actionCreators.SubmitCancelTask,
}>;

type Props = $Exact<{
  searchDisabled?: boolean,
  taskId?: ?number,
  taskType?: string,
  clientId: number,
  employeeId?: number,
  getTasks: Function, // On Page or filter change
  handleSelectTask: Function,
  tasks: Array<OutstandingTask>,
  pagination: PaginationExtendedType,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

type ComponentState = $Exact<{
  isTasksHidden: boolean,
  showTaskModal: boolean,
}>;
export class OutstandingTasks extends Component<Props, ComponentState> {
  state = {
    isTasksHidden: false,
    showTaskModal: false,
    selectedTaskId: null,
  };

  componentDidMount() {
    this.props.getTasks({
      clientId: this.props.clientId,
      search: {
        employee: this.props.tasksSearch,
      },
    });
  }

  toggleTasksSection = () => {
    this.setState({ isTasksHidden: !this.state.isTasksHidden });
  };
  toggleTaskModal = (row?: Object) => {
    if (!this.state.showTaskModal && row) this.props.handleSelectTask(row.id);

    this.setState({
      ...this.state,
      showTaskModal: !this.state.showTaskModal,
    });
  };

  get taskRenderComponent() {
    let selectedDetailsViews = false;
    if (!this.props.selectedTask) return selectedDetailsViews;
    else {
      switch (this.props.selectedTask.type) {
        case "RTW_INTERVIEW":
          selectedDetailsViews = (
            <RTWTaskDetailsSection
              toggleTasksSection={this.toggleTasksSection}
              isTasksHidden={this.state.isTasksHidden}
              selectedTask={this.props.selectedTask}
              clientId={this.props.clientId}
              employeeId={this.props.employeeId}
            />
          );
          break;
        case "ABSENCE_MONITORING_REVIEW":
          selectedDetailsViews = (
            <AbsenceTaskDetailsSection
              toggleTasksSection={this.toggleTasksSection}
              isTasksHidden={this.state.isTasksHidden}
              selectedTask={this.props.selectedTask}
              clientId={this.props.clientId}
              submitCancelTask={this.toggleTaskModal}
              employeeId={this.props.employeeId}
            />
          );
          break;
        case "MANAGER_REFERRAL_DECISION":
          selectedDetailsViews = (
            <ManagerReferralDecisionTask
              clientId={this.props.clientId}
              selectedTask={this.props.selectedTask}
              toggleTasksSection={this.toggleTasksSection}
              isTasksHidden={this.state.isTasksHidden}
              employeeId={this.props.employeeId}
            />
          );
          break;
        default:
          selectedDetailsViews = <div />;
      }

      return selectedDetailsViews;
    }
  }

  handleViewRecordClick = () => {
    window.open(
      route("REPORTING_EMPLOYEE_ABSENCES_PATH", {
        clientId: this.props.clientId,
        employeeId: this.props.selectedTask.employee.id,
      }),
      "_blank",
    );
  };

  status(record: OutstandingTask) {
    //$FlowIgnore
    const dueToday = moment(record.dueDate).isSame(moment(), "day"); //TODO our moment() is old so there is second arg
    if (record.isOverdue) {
      return "OVERDUE";
    } else if (dueToday) {
      return "DUE_TODAY";
    } else {
      return "TODO";
    }
  }

  getTypeBackgroundColor = (type: string) => {
    const typeColors = {
      ABSENCE_MONITORING_REVIEW: "background-color-ice",
      RTW_INTERVIEW: "background-color-coffee",
      MANAGER_REFERRAL_DECISION: "background-color-berry",
    };

    return typeColors[type] || "background-color-coffee";
  };

  get columnDefs() {
    return [
      {
        key: "id",
        width: 90,
        name: this.props.t("ID"),
      },
      {
        name: this.props.t("KEY"),
        key: "key",
        width: 60,
        renderCell: ({ row }: Object) => {
          const types = {
            RTW_INTERVIEW: "feed",
            ABSENCE_MONITORING_REVIEW: "assessment",
            MANAGER_REFERRAL_DECISION: "monitor_heart",
          };
          return <div className="d-flex material-icons">{types[row.type]}</div>;
        },
      },
      {
        key: "type",
        name: this.props.t("TYPE"),
        align: "left",
        width: 220,
        renderCell: ({ row }: Object) =>
          GSGrid.render.statusPill(
            this.props.t(row.type || ""),
            null,
            `w-170-px text-color-black ${this.getTypeBackgroundColor(row.type)}`,
          ),
      },
      {
        key: "employee.name",
        align: "left",
        name: this.props.t("EMPLOYEE_NAME"),
        renderCell: ({ row }: Object) => GSGrid.render.ellipsis(row.employee.name),
      },
      {
        key: "absenceId",
        width: 100,
        name: this.props.t("ABSENCE_ID"),
      },
      {
        key: "absenceStart",
        align: "left",
        width: 160,
        name: this.props.t("ABSENCE_START_DATE"),
        renderCell: ({ row }: Object) => formatDateTime(row.absenceStart),
      },
      {
        key: "absenceReason",
        align: "left",
        name: this.props.t("ABSENCE_REASON"),
        renderCell: ({ row }: Object) => row.absenceReason.name,
      },
      {
        key: "dueDate",
        align: "left",
        width: 160,
        name: this.props.t("DUE_DATE"),
        renderCell: ({ row }: Object) => formatDateTime(row.dueDate),
      },
      {
        key: "status",
        width: 150,
        name: this.props.t("STATUS"),
        renderCell: ({ row }: Object) => GSGrid.render.statusPill(this.props.t(this.status(row))),
      },
      {
        name: this.props.t("ACTION"),
        key: "action",
        width: 60,
        frozen: "right",
        onCellClick: (row: Object) =>
          row.canBeCompletedByCurrentLoggedInUser === true ? this.toggleTaskModal(row) : null,
        renderCell: ({ row }: Object) =>
          row.canBeCompletedByCurrentLoggedInUser === true
            ? GSGrid.render.materialIcon("play_circle")
            : null,
      },
    ];
  }

  onPageChange = (page: number) => {
    if (this.props.tasksType) {
      return this.props.getTasks({
        clientId: this.props.clientId,
        page,
        search: {
          taskType: this.props.tasksType,
          employee: this.props.tasksSearch,
        },
      });
    }

    return this.props.getTasks({ clientId: this.props.clientId, page });
  };
  filterTasks = (type?: string) => {
    this.onTasksTypeChange(type);
    if (type) {
      return this.props.getTasks({
        clientId: this.props.clientId,
        search: {
          employee: this.props.tasksSearch,
          taskType: type,
        },
      });
    }

    return this.props.getTasks({
      clientId: this.props.clientId,
      search: {
        employee: this.props.tasksSearch,
      },
    });
  };
  taskStatus = (status: string) => {
    return status === "DRAFT" ? this.props.t("TASK_IN_PROGRESS") : "";
  };
  onSearchTasksChange = (value: string) => {
    this.props.setMyTasksSearch(value);
  };
  onTasksTypeChange = (value?: string) => {
    this.props.setMyTasksType(value || null);
  };

  onChangePageTopNavigation = (pageNumber: number) => {
    this.onPageChange(pageNumber);
  };

  submitCancelTask = () => {
    this.props.submitCancelTask({
      clientId: this.props.clientId,
      taskId: this.props.selectedTask.id,
    });
    this.setState({ selectedTaskId: null });
  };

  get isAllowedToCancel() {
    const allowedUserTypes = ["ADMIN", "SERVICE_MANAGER", "CLIENT_ADMIN"];
    return (
      allowedUserTypes.indexOf(this.props.userType) !== -1 &&
      this.props.selectedTask.type === "ABSENCE_MONITORING_REVIEW"
    );
  }

  toggleCancelTaskModal = () => {
    this.setState({
      selectedTaskId: !this.state.selectedTaskId ? this.props.selectedTask.id : null,
    });
  };

  render() {
    const selectedTask = this.props.selectedTask;

    return (
      <View>
        {this.props.isLoading && <Loader />}
        <SectionComponent>
          {!this.props.searchDisabled && (
            <TasksQueueFilter
              className="mb-5"
              currentPage={this.props.pagination.pageNumber}
              pagesCount={this.props.pagination.totalPages}
              showingOption={this.props.t("SHOWING_ALL")}
              groupedBy={this.props.t("GROUPED_BY_ABSENCE")}
              toggleModalOptions={this.toggleModalOptions}
              filterTasks={this.filterTasks}
              onSearchTasksChange={this.onSearchTasksChange}
              searchValue={this.props.tasksSearch}
              tasksType={this.props.tasksType}
            />
          )}

          <GSGrid columns={this.columnDefs} rows={this.props.tasks} />
          <Pagination
            currentPage={this.props.pagination.pageNumber}
            totalPages={this.props.pagination.totalPages}
            onChange={this.onChangePageTopNavigation}
          />
        </SectionComponent>

        {!this.props.isLoading &&
          this.state.showTaskModal &&
          selectedTask &&
          this.taskRenderComponent && (
            <Modal onToggle={this.toggleTaskModal} rightPanel width={"50vw"}>
              <View flex={1} padding={[0, 6, 6, 6]} gap={3}>
                {/* Modal header */}
                <View
                  direction="row"
                  separator="bottom"
                  padding={[3, 0, 3, 0]}
                  backgroundColor={"white"}
                  position="sticky"
                  zIndex={5}
                  top={0}
                  justifyContent="space-between"
                >
                  <View direction="row" alignItems="center" gap={2}>
                    <h3>{this.props.t("TASK_ID") + ": " + selectedTask.id}</h3>
                    <View
                      className={classNames(
                        "gs-badge",
                        this.getTypeBackgroundColor(selectedTask.type),
                        {
                          "w-200-px": selectedTask.type === "ABSENCE_MONITORING_REVIEW",
                          "w-140-px": selectedTask.type !== "ABSENCE_MONITORING_REVIEW",
                        },
                      )}
                      justifyContent="center"
                    >
                      {this.props.t(selectedTask.type)}
                    </View>
                  </View>
                  <View direction="row" gap={3}>
                    <View direction="row">
                      {this.isAllowedToCancel && (
                        <Button className="mr-2" onClick={this.toggleCancelTaskModal}>
                          {this.props.t("CANCEL_TASK")}
                        </Button>
                      )}
                      <Button materialIcon="perm_identity" onClick={this.handleViewRecordClick}>
                        {this.props.t("EMPLOYEE")}
                      </Button>
                    </View>
                    <Button
                      onClick={this.toggleTaskModal}
                      color="secondary"
                      label={this.props.t("CANCEL")}
                    />
                  </View>
                </View>

                <View>
                  {/* Modal content */}
                  {this.taskRenderComponent}
                </View>
              </View>
            </Modal>
          )}

        {this.state.selectedTaskId && (
          <ConfirmationModal
            headerText={this.props.t("CANCEL_TASK")}
            bodyContent={this.props.t("CANCEL_TASK_MODAL_QUESTION")}
            onConfirm={this.submitCancelTask}
            onCancel={this.toggleCancelTaskModal}
            confirmBtnLabel={this.props.t("CANCEL_TASK")}
          />
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    isLoading: selectors.isLoading(state),
    total: selectors.getMyTasksTotalCount(state),
    selectedTask: selectors.getSelectedTask(state),
    tasksSearch: selectors.selectMyTasksSearch(state),
    tasksType: selectors.selectMyTasksType(state),
    userType: selectUserType(state),
  };
};

const mapDispatchToProps = {
  setMyTasksSearch: actionCreators.SetMyTasksSearch,
  setMyTasksType: actionCreators.SetMyTasksType,
  submitCancelTask: actionCreators.SubmitCancelTask,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(OutstandingTasks));
